import { Button } from '../Button';
import { PublishListingDialog } from './details/components/PublishListingDialog';
import { Award, FileText } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDappDataOptional } from '../../contexts/DappDataContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatRequiredFieldsLeft } from '../../utils/storeListingCompleteness';
import { useDappContext } from '../DappLayout';
import {
  PortalCard,
  PortalPage,
  PortalPageHeader,
  PortalTabs,
  type PortalTabDefinition,
  StatusDot,
} from '../portal';
import {
  DashboardDetailsForm,
  STORE_LISTING_TABS_ID,
  type StoreListingTab,
} from './details/components/DashboardDetailsForm';
import { DiscardChangesDialog } from './details/components/DiscardChangesDialog';
import { StoreListingActions } from './details/components/StoreListingActions';
import {
  StoreListingActionsSkeleton,
  StoreListingSkeleton,
} from './details/components/StoreListingSkeleton';
import { FORM_ID } from './details/constants';
import { useDashboardDetailsForm } from './details/hooks/useDashboardDetailsForm';

const TABS: PortalTabDefinition<StoreListingTab>[] = [
  { key: 'details', label: 'App Details', icon: <FileText /> },
  { key: 'editors-choice', label: "Editor's Choice", icon: <Award /> },
];

export const DashboardDetails = () => {
  const { dappId } = useDappContext();
  const { dappData } = useDappDataOptional();
  const dappName = dappData?.dappName || null;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: StoreListingTab =
    searchParams.get('tab') === 'editors-choice' ? 'editors-choice' : 'details';
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);

  usePageTitle(
    dappId && dappName
      ? `${dappName} - Store Listing | Publishing Portal`
      : 'Store Listing | Publishing Portal',
  );

  const form = useDashboardDetailsForm({ dappId });

  const missingCount = form.missingRequiredFields.length;
  const trailingText =
    missingCount > 0
      ? formatRequiredFieldsLeft(missingCount)
      : form.hasChanges
        ? 'Unsaved changes'
        : null;
  // Keyed on the copy so a change fades in again instead of snapping.
  const trailing =
    !form.isLoading && trailingText ? (
      <span
        key={trailingText}
        className="flex animate-in items-center gap-1 text-orange-400 tabular-nums fade-in ease-out animation-duration-150"
      >
        <StatusDot />
        {trailingText}
      </span>
    ) : null;

  // The header, card and tabs are static chrome: they render immediately so
  // only the form body and the action buttons swap when `getDapp` lands.
  return (
    <PortalPage>
      <PortalPageHeader
        icon={<FileText />}
        title="Store Listing"
        description={
          form.isInitialDraft
            ? 'Complete your listing to continue setting up your app, or save a draft for later.'
            : 'Publish listing changes as a new release, or save a draft for later.'
        }
        actions={
          form.isLoading ? (
            <StoreListingActionsSkeleton />
          ) : (
            <StoreListingActions
              formId={FORM_ID}
              isInitialDraft={form.isInitialDraft}
              hasChanges={form.hasChanges}
              canPublish={
                missingCount === 0 &&
                form.isFormValid &&
                form.hasUnpublishedChanges
              }
              hasDraft={form.hasDraft}
              justSavedDraft={form.justSavedDraft}
              justPublished={form.justPublished}
              isSubmitting={form.isSubmitting}
              isSavingDraft={form.isSavingDraft}
              lastPublishedAt={form.lastPublishedAt}
              onDiscard={() => setIsDiscardOpen(true)}
              onSaveDraft={() => void form.handleSaveDraft()}
            />
          )
        }
      />

      {form.hasUnfinishedRelease && !form.isSubmitting && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-seeker-800 px-5 py-3 text-sm text-seeker-400">
          <p>
            You have an unfinished release. Continue it before submitting
            another update.
          </p>
          <Button
            variant="outlineMuted"
            onClick={() => void form.handleViewRelease()}
            disabled={form.isSavingDraft}
          >
            {form.hasChanges ? 'Save draft & view release' : 'View release'}
          </Button>
        </div>
      )}
      <PortalCard>
        <PortalTabs
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={(tab) =>
            setSearchParams(
              (previous) => {
                const next = new URLSearchParams(previous);
                if (tab === 'details') {
                  next.delete('tab');
                } else {
                  next.set('tab', tab);
                }
                return next;
              },
              { replace: true },
            )
          }
          idPrefix={STORE_LISTING_TABS_ID}
          ariaLabel="Store Listing sections"
          trailing={trailing}
        />

        {form.isLoading ? (
          <StoreListingSkeleton />
        ) : (
          <DashboardDetailsForm
            isBusy={form.isSavingDraft || form.isSubmitting}
            showWalletField={form.needsAppWallet}
            formKey={form.formKey}
            activeTab={activeTab}
            formData={form.formData}
            previewPanelData={form.previewPanelData}
            previewUploads={form.previewUploads}
            resolvedDappIconUrl={form.resolvedDappIconUrl}
            resolvedBannerUrl={form.resolvedBannerUrl}
            resolvedPreviewUrls={form.resolvedPreviewUrls}
            resolvedEditorsChoiceGraphicUrl={
              form.resolvedEditorsChoiceGraphicUrl
            }
            existingPreviewUrls={form.existingPreviewUrls}
            onValidityChange={form.setIsFormValid}
            onInputChange={form.handleInputChange}
            onSingleFileChange={form.handleSingleFileChange}
            onRemoveExistingDappIcon={form.removeExistingDappIcon}
            onRemoveExistingBanner={form.removeExistingBanner}
            onRemoveExistingPreview={form.removeExistingPreview}
            onRemoveExistingGraphic={form.removeExistingGraphic}
            onSubmit={form.handleSubmit}
          />
        )}
      </PortalCard>

      <PublishListingDialog
        open={form.isPublishOpen}
        state={form.publicationState}
        busy={form.isSubmitting}
        isSavingDraft={form.isSavingDraft}
        onConnectWallet={form.openWalletPicker}
        progress={form.publicationProgress}
        error={form.publicationError}
        onViewRelease={() => void form.handleViewRelease()}
        hasChanges={form.hasChanges}
        walletReady={form.walletReady}
        walletMessage={form.walletMessage}
        onConfirm={() => void form.handlePublishConfirm()}
        onClose={form.closePublishDialog}
      />
      <DiscardChangesDialog
        open={isDiscardOpen}
        onConfirm={() => {
          void form.handleDiscard();
          setIsDiscardOpen(false);
        }}
        onCancel={() => setIsDiscardOpen(false)}
      />
    </PortalPage>
  );
};
